import {Validator} from 'vee-validate';
import {itValidation} from '@/i18n/it';
import {enValidation} from '@/i18n/en';
import {esValidation} from '@/i18n/es';
import {deValidation} from '@/i18n/de';
import {frValidation} from '@/i18n/fr';
import validateFiscalCode from '@/commons/fiscalCodeValidator';
import store from '@/store';
import checkout from '@/store/modules/checkout';

function validateCf(value: string) {
    const cf = value.toUpperCase();

    if (cf === '') {
        return true;
    }

    if (!/^[0-9A-Z]{16}$/.test(cf)) {
        return false;
    }

    const map = [
        1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10,
        22, 25, 24, 23,
    ];

    let s = 0;

    for (let i = 0; i < 15; i += 1) {
        let c = cf.charCodeAt(i);

        if (c < 65) {
            c -= 48;
        } else {
            c -= 55;
        }

        if (i % 2 === 0) {
            s += map[c];
        } else {
            s += c < 10 ? c : c - 10;
        }
    }

    const expected = String.fromCharCode(65 + (s % 26));

    if (expected !== cf.charAt(15)) {
        return false;
    }

    return true;
}

function validatePiva(value: string) {
    const pi = value;

    if (pi === '') {
        return true;
    }

    if (!/^[0-9]{11}$/.test(pi)) {
        return false;
    }

    let s = 0;

    for (let i = 0; i <= 9; i += 2) {
        s += pi.charCodeAt(i) - '0'.charCodeAt(0);
    }

    for (let i = 1; i <= 9; i += 2) {
        let c = 2 * (pi.charCodeAt(i) - '0'.charCodeAt(0));
        if (c > 9) {
            c -= 9;
        }
        s += c;
    }

    const expected = (10 - (s % 10)) % 10;

    if (expected !== pi.charCodeAt(10) - '0'.charCodeAt(0)) {
        return false;
    }

    return true;
}

function validateTelephoneWithoutPrefix(value: string) {
    return /^[0-9]{3,10}$/.test(value);
}

function validateForeignCountryTelephoneWithoutPrefix(value: string) {
    return /^[\d ]+$/.test(value);
}

function validateOneyPhone(value: string) {
    return /^((?:0039)?|(?:\+39)?)3[0-9]{8,9}$/.test(value);
}

function validateName(value: string) {
    const regex = /([\p{L}\p{M},._'\s\d-]){1,255}/u;
    const match = regex.exec(value);

    if (match !== null) {
        return match[0] === value;
    }

    return false;
}

function validateVatNumber(value: string) {
    if (store.state.options.store_code === 'de_de') {
        switch (checkout.billingAddress.country_id) {
            case 'DE':
                return /^DE\d{9}$/.test(value);
            default:
                return true;
        }
    }

    return true;
}

export default (i18n: any) => {
    Validator.extend('validate-cf', {
        getMessage: () => 'Codice fiscale non valido',
        validate: (value) => {
            return validateCf(value);
        },
    });

    Validator.extend('validate-fiscal-code', {
        getMessage: () => 'Codice fiscale non valido',
        validate: (value) => {
            return validateFiscalCode(value);
        },
    });

    Validator.extend('validate-piva', {
        getMessage: () => 'Partita IVA non valida.',
        validate: (value) => {
            return validatePiva(value);
        },
    });

    Validator.extend('validate-piva-cf', {
        getMessage: () => 'Partita IVA / Codice fiscale non valido.',
        validate: (value) => {
            return validatePiva(value) || validateCf(value);
        },
    });

    Validator.extend('validate-telephone-without-prefix', {
        getMessage: () => 'Numero di telefono non valido',
        validate: (value) => {
            return validateTelephoneWithoutPrefix(value);
        },
    });

    Validator.extend('validate-foreign-country-telephone-without-prefix', {
        getMessage: () => 'Numero di telefono non valido',
        validate: (value) => {
            return validateForeignCountryTelephoneWithoutPrefix(value);
        },
    });

    Validator.extend('validate-oney-telephone', {
        getMessage: () => 'Numero di telefono per oney non valido.',
        validate: (value) => {
            return validateOneyPhone(value);
        },
    });

    Validator.extend('validate-firstname', {
        getMessage: () => 'Il Nome contiene caratteri non validi.',
        validate: (value) => {
            return validateName(value);
        },
    });

    Validator.extend('validate-lastname', {
        getMessage: () => 'Il Cognome contiene caratteri non validi.',
        validate: (value) => {
            return validateName(value);
        },
    });

    Validator.extend('validate-regionid', {
        getMessage: () => 'Il campo Provincia è richiesto',
        validate: (value) => {
            return value && value > 0;
        },
    });

    Validator.extend('validate-length', {
        getMessage: () => 'Lunghezza campo non valida ',
        validate: (value, args: any) => {
            const length = args[0];
            return value.length === length;
        },
    });

    Validator.extend('validate-vat-number', {
        getMessage: () => 'Partita IVA non valida',
        validate: (value) => {
            return validateVatNumber(value);
        },
    });

    return {
        events: '',
        classes: true,
        classNames: {
            valid: 'valid',
            invalid: 'mage-error',
        },
        aria: false,
        i18n,
        dictionary: {
            it: itValidation,
            en: enValidation,
            es: esValidation,
            de: deValidation,
            fr: frValidation,
        },
    };
};
